import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';
import { getProjects, getTasks } from '../api';

export default function FilesPage() {
  const navigate = useNavigate();
  const [files,    setFiles]    = useState([]);
  const [projects, setProjects] = useState([]);
  const [tasks,    setTasks]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [fRes, pRes, tRes] = await Promise.all([
          api.get('/wp/v2/media?per_page=100&orderby=date&order=desc'),
          getProjects(),
          getTasks(),
        ]);
        setFiles(fRes.data);
        setProjects(pRes.data);
        setTasks(tRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // ── Match file to task/project ──
  const getFileContext = (file) => {
    // Search tasks for this file ID
    for (const task of tasks) {
      const ids = JSON.parse(task.meta?.gpf_file_ids || '[]');
      if (ids.includes(file.id)) {
        const project = projects.find(p => p.id === task.meta?.gpf_project_id);
        return {
          type:        'task',
          taskName:    task.title?.rendered || 'Untitled Task',
          taskId:      task.id,
          projectName: project?.title?.rendered || 'Unknown Project',
          projectId:   task.meta?.gpf_project_id,
        };
      }
    }
    return null;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fileIcon = (mimeType = '') => {
    if (mimeType.startsWith('image/'))                              return { icon: '🖼', color: '#7c3aed' };
    if (mimeType.includes('pdf'))                                   return { icon: '📄', color: '#dc2626' };
    if (mimeType.includes('word') || mimeType.includes('document')) return { icon: '📝', color: '#2563eb' };
    if (mimeType.includes('sheet') || mimeType.includes('excel'))   return { icon: '📊', color: '#16a34a' };
    if (mimeType.includes('zip') || mimeType.includes('compressed'))return { icon: '🗜', color: '#d97706' };
    if (mimeType.includes('presentation'))                          return { icon: '📊', color: '#ea580c' };
    return { icon: '📎', color: '#64748b' };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ── Filter + search ──
  const enriched = files.map(f => ({ ...f, context: getFileContext(f) }));

  const filtered = enriched.filter(f => {
    const matchSearch = !search ||
      f.title?.rendered?.toLowerCase().includes(search.toLowerCase()) ||
      f.context?.taskName?.toLowerCase().includes(search.toLowerCase()) ||
      f.context?.projectName?.toLowerCase().includes(search.toLowerCase());

    const matchFilter =
      filter === 'all'   ? true :
      filter === 'linked'   ? !!f.context :
      filter === 'unlinked' ? !f.context :
      f.mime_type?.startsWith(filter);

    return matchSearch && matchFilter;
  });

  const totalSize = files.reduce((sum, f) => sum + (f.media_details?.filesize || 0), 0);

  return (
    <Layout>
      <div style={styles.page}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Documents</h1>
            <p style={styles.subtitle}>
              {files.length} document{files.length !== 1 ? 's' : ''} · {formatSize(totalSize)} total
            </p>
          </div>
        </div>

        {/* Search + filter */}
        <div style={styles.toolbar}>
          <input
            style={styles.searchInput}
            placeholder="Search files, tasks, projects..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={styles.tabs}>
            {[
              { key: 'all',      label: 'All' },
              { key: 'linked',   label: 'Linked to Tasks' },
              { key: 'image/',   label: 'Images' },
              { key: 'application/pdf', label: 'PDFs' },
              { key: 'unlinked', label: 'Unlinked' },
            ].map(f => (
              <button
                key={f.key}
                style={{ ...styles.tab, ...(filter === f.key ? styles.tabActive : {}) }}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <p style={styles.loading}>Loading files...</p>
        ) : filtered.length === 0 ? (
          <div style={styles.empty}>
            <p style={styles.emptyText}>
              {search ? 'No files match your search.' : 'No files uploaded yet.'}
            </p>
          </div>
        ) : (
          <div style={styles.fileList}>
            <div style={styles.tableHeader}>
              <span style={{ flex: 3 }}>File</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Type</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Size</span>
              <span style={{ flex: 2 }}>Linked To</span>
              <span style={{ flex: 1 }}>Uploaded</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
            </div>

            {filtered.map(file => {
              const { icon, color } = fileIcon(file.mime_type);
              const ctx = file.context;

              return (
                <div key={file.id} style={styles.fileRow}>
                  {/* File name + icon */}
                  <div style={{ flex: 3, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ ...styles.fileIconBox, background: color + '15', color }}>{icon}</span>
                    <div style={styles.fileInfo}>
                      <a href={file.source_url} target="_blank" rel="noreferrer" style={styles.fileName}>
                        {file.title?.rendered || file.slug || 'Untitled'}
                      </a>
                    </div>
                  </div>

                  {/* MIME type */}
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <span style={{ ...styles.typeBadge, color }}>
                      {file.mime_type?.split('/')[1]?.toUpperCase() || '—'}
                    </span>
                  </div>

                  {/* Size */}
                  <div style={{ flex: 1, textAlign: 'center', fontSize: 13, color: '#64748b' }}>
                    {formatSize(file.media_details?.filesize)}
                  </div>

                  {/* Linked to */}
                  <div style={{ flex: 2 }}>
                    {ctx ? (
                      <div style={styles.contextBox}>
                        <span
                          style={styles.contextProject}
                          onClick={() => navigate(`/projects/${ctx.projectId}/tasks`)}
                          title={ctx.projectName}
                        >
                          {ctx.projectName?.toUpperCase()}
                        </span>
                        <span style={styles.contextArrow}>›</span>
                        <span style={styles.contextTask} title={ctx.taskName}>
                          {ctx.taskName}
                        </span>
                      </div>
                    ) : (
                      <span style={styles.unlinked}>Not linked</span>
                    )}
                  </div>

                  {/* Uploaded date */}
                  <div style={{ flex: 1, fontSize: 13, color: '#64748b' }}>
                    {formatDate(file.date)}
                  </div>

                  {/* Actions */}
                  <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <a
                      href={file.source_url}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.downloadBtn}
                      title="Download"
                    >
                      ↓
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}

const styles = {
  page:        { maxWidth: 1100 },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { fontSize: 24, fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle:    { color: '#64748b', fontSize: 14, margin: '4px 0 0' },
  toolbar:     { display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' },
  searchInput: { padding: '9px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', width: 260 },
  tabs:        { display: 'flex', gap: 4, background: '#fff', padding: 4, borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', flexWrap: 'wrap' },
  tab:         { padding: '5px 12px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: '#64748b' },
  tabActive:   { background: '#2563eb', color: '#fff' },
  loading:     { color: '#64748b', fontSize: 14 },
  empty:       { background: '#fff', borderRadius: 10, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  emptyText:   { color: '#94a3b8', fontSize: 14 },
  fileList:    { background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  tableHeader: { display: 'flex', alignItems: 'center', padding: '12px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  fileRow:     { display: 'flex', alignItems: 'center', padding: '14px 20px', borderBottom: '1px solid #f1f5f9', gap: 8 },
  fileIconBox: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 },
  fileInfo:    { minWidth: 0 },
  fileName:    { fontSize: 14, fontWeight: 600, color: '#0f172a', textDecoration: 'none', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 },
  typeBadge:   { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em' },
  contextBox:  { display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 },
  contextProject: { fontSize: 11, fontWeight: 700, color: '#2563eb', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 },
  contextArrow:   { color: '#94a3b8', fontSize: 12 },
  contextTask:    { fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120 },
  unlinked:    { fontSize: 12, color: '#cbd5e1', fontStyle: 'italic' },
  downloadBtn: { width: 28, height: 28, border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2563eb', textDecoration: 'none' },
};